/**
 * 이 파일은 Aui 의 일부입니다. (https://aui.moimz.tools)
 *
 * 테이블 그룹 클래스를 정의한다.
 *
 * @file /src/models/table/Group.ts
 * @author Arzz <arzz@arzz.com>
 * @license MIT License
 * @modified 2026. 6. 14.
 */
import { Table } from '@models/table/Table';
import { Record } from '@models/store/Record';
import { Summary } from '@models/table/Summary';
import { Chunks } from '@utils/Chunks';
import { Dom } from '@utils/Dom';
import { Html } from '@utils/Html';
import './Group.css';

export interface Grouper<T> {
    dataIndex: string;
    sorters: { [field: string]: 'ASC' | 'DESC' | any[] };
    summary?: boolean;
    renderer?: (value: any, dataIndex: string, records: Record[], grid: T) => string | Promise<string>;
}

export interface Properties {
    /**
     * 테이블 객체
     */
    table: Table;

    /**
     * 그룹핑된 데이터인덱스 값
     */
    dataIndexValue: string;
}

export class Group {
    /**
     * 테이블 객체
     */
    table: Table;

    /**
     * 그룹핑된 데이터인덱스 값
     */
    dataIndexValue: string;

    /**
     * 레코드고유값
     */
    recordIds: string[] = [];

    /**
     * 그룹 Dom
     */
    $group: Dom = null;

    /**
     * 그룹명 레이아웃 Dom
     */
    $title: Dom = null;

    /**
     * 합계
     */
    summary: Summary;

    /**
     * row observer
     */
    observer = new IntersectionObserver(
        (entries) => {
            entries.forEach((entry) => {
                if (entry.isIntersecting == true) {
                    const row = this.getTable().getBody().getRowById(entry.target.getAttribute('data-id'));
                    row.connect();
                } else {
                    //row.disconnect();
                }
            });
        },
        { threshold: [0, 0.1] }
    );

    /**
     * 테이블그룹 클래스를 정의한다.
     *
     * @param properties - 초기설정
     */
    constructor(public properties: Properties) {
        this.table = properties.table;
        this.dataIndexValue = properties.dataIndexValue;
        this.recordIds = [];
        this.summary = new Summary(this.table);
    }

    /**
     * 테이블을 가져온다.
     *
     * @returns 테이블
     */
    getTable(): Table {
        return this.table;
    }

    /**
     * 그룹에 속한 레코드고유값을 설정한다.
     *
     * @param recordIds - 레코드고유값 목록
     */
    setRecords(recordIds: string[]): void {
        this.recordIds = recordIds;
    }

    /**
     * 그룹에 속한 첫번째 레코드를 가져온다.
     *
     * @returns 그룹에 속한 첫번째 레코드
     */
    getFirstRecord(): Record | null {
        if (this.recordIds.length == 0) {
            return null;
        }

        return this.getTable().getStore().get(this.recordIds[0]);
    }

    /**
     * 그룹 Dom 을 가져온다.
     *
     * @returns 그룹 Dom
     */
    $getGroup(): Dom {
        if (this.$group === null) {
            this.$group = Html.create('div', {
                'data-model': 'table',
                'data-role': 'group',
                'data-is-lazy-connect': 'true',
            });
        }

        return this.$group;
    }

    /**
     * 그룹명 레이아웃 Dom을 가져온다.
     *
     * @param records - 그룹에 속한 레코드
     * @returns 그룹명 레이아웃 Dom
     */
    $getTitle(records: Record[]): Dom {
        if (this.$title === null) {
            this.$title = Html.create('div', { 'data-model': 'table', 'data-role': 'title' });
            const $label = Html.create('label');

            const groupFeild = this.getTable().getStore().getGroupField();

            if (this.getTable().getGrouper().renderer !== null) {
                const text = this.getTable()
                    .getGrouper()
                    .renderer(records[0].get(groupFeild), groupFeild, records, this.getTable());

                if (typeof text != 'string') {
                    if (typeof text.then == 'function') {
                        text.then((text) => {
                            $label.html(text);
                        });
                    }
                } else {
                    $label.html(text);
                }
            } else {
                $label.html(records[0].get(groupFeild));
            }

            this.$title.append($label);

            $label.on('click', () => {
                this.$getGroup().toggleClass('collapsed');
            });
        }

        return this.$title;
    }

    /**
     * summary 내 컬럼을 고정한다.
     */
    setFreeze(): void {
        this.summary.setFreeze();
    }

    /**
     * 일정 개수씩 Row를 DocumentFragment에 렌더링하여 DOM에 추가한다.
     * requestAnimationFrame을 활용하여 한 프레임에 너무 많은 렌더링이 발생하지 않도록 분산 처리함.
     *
     * @param records - 그룹에 해당하는 행
     * @returns 모든 Row가 렌더링 완료된 후 resolve되는 Promise
     */
    async renderRows(records: Record[]): Promise<void> {
        const chunks = new Chunks<Record>(records);
        const isTree = this.getTable().isTree();

        if (this.getTable().getBody().isLazyRender == true) {
            await chunks.execute(
                (record) => {
                    const row = this.getTable().getBody().getRow(record);
                    if (row === null) {
                        return;
                    }

                    if (isTree == true) {
                        const $tree = row.$getTree();
                        this.observer.observe($tree.getEl());
                        this.$getGroup().append($tree);
                    } else {
                        row.connect();
                        this.$getGroup().append(row.$getRow());
                    }
                },
                'Render Rows (' + chunks.getTotal() + 'Rows)'
            );
        } else {
            const fragment = document.createDocumentFragment();
            await chunks.execute(
                (record) => {
                    const row = this.getTable().getBody().getRow(record);
                    if (row === null) {
                        return;
                    }
                    row.connect();
                    fragment.append(isTree == true ? row.$getTree().getEl() : row.$getRow().getEl());
                },
                'Render Rows (' + chunks.getTotal() + 'Rows)'
            );

            this.$getGroup().getEl().append(fragment);
        }
    }

    /**
     * 데이터를 렌더링한다.
     */
    async render(): Promise<void> {
        this.$group.empty();

        const records: Record[] = [];
        for (const recordId of this.recordIds) {
            records.push(this.getTable().getStore().get(recordId));
        }

        if (this.getTable().getGrouper() !== null) {
            this.$getGroup().append(this.$getTitle(records));
        }

        await this.renderRows(records);

        if (this.getTable().getGrouper()?.summary === true) {
            this.$getGroup().append(this.summary.$getSummary(records));
        }
    }

    /**
     * 객체를 제거한다.
     */
    destroy(): void {
        this.$group = null;
        this.$title = null;
        this.table = null;
        this.recordIds = [];
        this.observer.disconnect();
        this.observer = null;
        this.summary.destroy();
        this.summary = null;
    }
}
